import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const field = "w-full rounded-xl border-2 border-slate-200 h-11 px-3 text-sm focus:border-[#2EC4B6] outline-none";

export default function PinRecovery({ onBack, onDone }) {
  const [method, setMethod] = useState("code");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    try { const { data } = await api.post("/settings/pin/forgot"); setSentTo(data.email); toast.success("Code sent — check your email"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send code"); }
    finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/settings/pin/reset", { method, code, password, new_pin: newPin });
      toast.success("New PIN saved 🔒");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not reset PIN"); }
    finally { setBusy(false); }
  };

  const tab = (v, label) => (
    <button type="button" onClick={() => setMethod(v)} data-testid={`pin-recovery-${v}-tab`}
      className={`flex-1 rounded-full py-2 text-xs font-bold transition-colors ${method === v ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{label}</button>
  );

  return (
    <form onSubmit={reset} className="text-left" data-testid="pin-recovery">
      <button type="button" onClick={onBack} data-testid="pin-recovery-back" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <h3 className="mt-2 text-xl font-display font-bold text-slate-900 text-center">Reset Parent PIN</h3>
      <div className="flex rounded-full bg-slate-100 p-1 mt-4">{tab("code", "Email me a code")}{tab("password", "Use my password")}</div>
      <div className="mt-4 space-y-3">
        {method === "code" ? (
          sentTo ? (
            <>
              <p className="text-xs text-slate-500">We sent a 6-digit code to <strong>{sentTo}</strong>.</p>
              <input data-testid="pin-recovery-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className={field} />
              <button type="button" onClick={sendCode} disabled={busy} data-testid="pin-recovery-resend" className="text-xs font-semibold text-[#2EC4B6] hover:underline">Resend code</button>
            </>
          ) : (
            <Button type="button" onClick={sendCode} disabled={busy} data-testid="pin-recovery-send-code" className="w-full h-11 rounded-xl font-bold bg-[#2EC4B6] hover:bg-[#26a69a]">
              {busy ? "Sending…" : "Send code to my email"}
            </Button>
          )
        ) : (
          <input data-testid="pin-recovery-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your account password" className={field} />
        )}
        {(method === "password" || sentTo) && (
          <>
            <input data-testid="pin-recovery-new-pin" inputMode="numeric" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="New PIN (4-6 digits)" className={field} />
            <Button type="submit" data-testid="pin-recovery-submit" disabled={busy || newPin.length < 4 || (method === "code" ? code.length !== 6 : !password)}
              className="w-full h-11 rounded-xl font-bold bg-[#2EC4B6] hover:bg-[#26a69a]">{busy ? "Saving…" : "Save new PIN"}</Button>
          </>
        )}
      </div>
    </form>
  );
}
